'use client'

import * as UI from '../footer.styled'
import {CallRequestData, CallRequestSchema} from '../footer.types'
import React, {useState} from 'react'
import {useFormContext} from 'react-hook-form'

import {useMutation} from '@tanstack/react-query'

import {Routes} from '@/src/config/routes'

import {FormProvider} from '@/src/shared/modules/form'

import {InvisibleCaptcha, useCaptcha} from '@/src/shared/modules/captcha'
import Button from '@/src/shared/ui/button'
import {LicenseAgreement} from '@/src/shared/ui/form/agreement'
import FormPhoneInput from '@/src/shared/ui/inputs/form-phone-input'
import {FormInput} from '@/src/shared/ui/inputs/input'
import {AsyncProcessLoading} from '@/src/shared/ui/layout/ui/async-loading'
import {getClient} from '@/src/shared/utils/api-factory'
import {observer} from 'mobx-react'
import {useLayoutStore} from '@/src/shared/ui/layout'
import {handleMutationException} from '@/src/shared/utils/error'

interface CallRequestModalProps {
  readonly open: boolean
  readonly onClose: () => void
}

export const CallRequestModal: React.FC<CallRequestModalProps> = observer(({open, onClose}) => {
  const layoutStore = useLayoutStore()

  return (
    <UI.FeedbackModal
      key={`callRequest-${open ? '+' : '-'}`}
      open={open}
      onCancel={onClose}
      title="Заказать звонок">
      <FormProvider
        schema={CallRequestSchema}
        data={{
          name: layoutStore.data.session?.user?.name,
          phone: layoutStore.data.session?.user?.phone,
        }}>
        <CallRequestForm />
      </FormProvider>
    </UI.FeedbackModal>
  )
})

const CallRequestForm: React.FC = () => {
  const form = useFormContext<CallRequestData>()
  const [formSubmitted, setSubmitted] = useState(false)

  const {token, onSuccessToken, visibleCaptcha, setVisibleCaptcha, renderKey, reset} = useCaptcha()

  const submitMutation = useMutation({
    mutationFn: async (data: CallRequestData) => {
      const client = getClient()
      await client.post(Routes.webApi.form.callback.save, {data})
    },
    async onSuccess() {
      setSubmitted(true)
    },
    async onError(error) {
      handleMutationException(error)
      reset()
    },
  })

  const onSubmit = form.handleSubmit(data => {
    submitMutation.mutate(data)
  })

  if (formSubmitted) {
    return (
      <UI.FeedbackForm $submitted>
        <p>Ваша заявка принята</p>
        <p>Наш менеджер свяжется с вами в рабочее время.</p>
        <p>Режим работы интернет-магазина: ежедневно с 9:00 до 20:00.</p>
      </UI.FeedbackForm>
    )
  }

  return (
    <UI.FeedbackForm $submitted={false}>
      <InvisibleCaptcha
        key={renderKey}
        renderKey={renderKey}
        onSuccess={onSuccessToken}
        visible={visibleCaptcha}
        setVisible={setVisibleCaptcha}
      />
      <FormInput name="name" required>
        Ваше имя
      </FormInput>
      <FormPhoneInput name="phone" required>
        Номер телефона
      </FormPhoneInput>
      <Button type="submit" onClick={onSubmit} disabled={!token} loading={!token}>
        Заказать звонок
      </Button>
      <LicenseAgreement btnName="Заказать звонок" />
      {submitMutation.isPending && <AsyncProcessLoading />}
    </UI.FeedbackForm>
  )
}
